import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { Shield, Lock, Ghost, ArrowRight, Loader, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import logo from "../../logo2.png";

const ALIAS_ADJECTIVES = [
  "Mysterious", "Happy", "Sleepy", "Creative", "Lucky", "Brave", "Calm", "Eager", "Witty", "Bold",
  "Campus", "Midnight", "Early", "Silent", "Loud", "Curious", "Hungry", "Chill", "Lost"
];
const ALIAS_NOUNS = [
  "Student", "Learner", "Explorer", "Owl", "Thinker", "Dreamer", "Goose", "Panda", "Tiger", "Falcon",
  "Reader", "Artist", "Gamer", "Walker", "Legend", "Scholar", "Genius", "Friend", "Neighbor"
];

const CAMPUS_MAPPING = {
  "@isbstudent.comsats.edu.pk": { name: "Islamabad", code: "CUI-ISB" },
  "@cuisahiwal.edu.pk": { name: "Sahiwal", code: "CUI-SWL" },
  "@cuiwah.edu.pk": { name: "Wah", code: "CUI-WAH" },
  "@cuilahore.edu.pk": { name: "Lahore", code: "CUI-LHR" },
  "@cuiatk.edu.pk": { name: "Attock", code: "CUI-ATK" },
  "@cuiatd.edu.pk": { name: "Abbottabad", code: "CUI-ATD" }
};

const CampusIdentity = ({ onVerified }) => {
  const [loading, setLoading] = useState(true); // Start loading immediately to check session
  const [user, setUser] = useState(null); // This tracks the *active* verified user
  const [sessionUser, setSessionUser] = useState(null); // This tracks the *detected* session
  const [showLoginOptions, setShowLoginOptions] = useState(false);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false); // Track if user wants to switch
  const [alias, setAlias] = useState("");

  // Email login states
  const [email, setEmail] = useState("");
  const [emailsent, setEmailSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  // Ref to prevent double-submission race conditions
  const verifyingRef = React.useRef(false);

  useEffect(() => {
    checkUser();
    
    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT') {
           setSessionUser(null);
           setShowLoginOptions(false);
           setUser(null);
        } else if (session?.user && !user && !showLoginOptions && !verifyingRef.current) {
           // Only react to session changes if we ARE NOT currently verifying manually
           checkUser(); 
        }
      }
    );
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const checkUser = async () => {
    try {
      setLoading(true);
      // Check local session first (faster & persistent)
      let { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (!currentUser) {
         const { data: { session } } = await supabase.auth.getSession();
         currentUser = session?.user;
      }
      
      if (currentUser) {
        // Fetch profile data
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("campus_alias, avatar_url, avatar_seed, bio, created_at, campus_code")
          .eq("id", currentUser.id)
          .single();
        
        if (error) {
           console.error("Error fetching profile:", error);
        }

        // AUTO-LOGIN LOGIC: 
        // If we have a profile and we're not explicitly switching, just log in!
        if (profile && !isSwitchingAccount) {
           onVerified(currentUser, profile.campus_alias, profile.campus_code);
           return; // Skip setting state which triggers re-render
        }

        // Store session info (fallback to selection screen if profile missing or switching)
        setSessionUser({ 
            user: currentUser, 
            profile: profile || {}
        });
        setShowLoginOptions(true);
      }
      setLoading(false);
    } catch (err) {
      console.error("Auth check failed:", err);
      setLoading(false);
    }
  };

  const handleContinueAsUser = async () => {
    if (!sessionUser) return;
    setLoading(true);
    await handleUser(sessionUser.user); // Proceed with standard verification
    setLoading(false);
  };

  const handleLoginAnother = async () => {
    // Just switch view state, don't sign out yet so we can "cancel"
    setIsSwitchingAccount(true);
    setEmail("");
  };

  const handleUser = async (currentUser) => {
    // 1. Domain Check (Strict Enforcement)
    const emailDomain = Object.keys(CAMPUS_MAPPING).find(domain => 
        currentUser.email.toLowerCase().endsWith(domain)
    );

    if (!emailDomain) {
      await supabase.auth.signOut();
      toast.error("Access restricted: You must use your official COMSATS university account.");
      return;
    }

    const campusInfo = CAMPUS_MAPPING[emailDomain];
    setUser(currentUser);
    
    // 2. Check/Create Profile
    // We might already have the profile from checkUser, but fetching again ensures freshness and follows existing flow
    const { data: profile } = await supabase
      .from("profiles")
      .select("campus_alias, campus_code")
      .eq("id", currentUser.id)
      .single();

    if (profile?.campus_alias) {
      setAlias(profile.campus_alias);
      
      // Update campus_code if it's missing (backfill)
      if (!profile.campus_code) {
          await supabase.from("profiles").update({ campus_code: campusInfo.code }).eq("id", currentUser.id);
      }
      onVerified(currentUser, profile.campus_alias, profile.campus_code || campusInfo.code);
    } else {
      // Generate new alias if none exists
      generateAndSaveAlias(currentUser, campusInfo);
    }
  };

  const generateAndSaveAlias = async (currentUser, campusInfo) => {
    const randomAdj = ALIAS_ADJECTIVES[Math.floor(Math.random() * ALIAS_ADJECTIVES.length)];
    const randomNoun = ALIAS_NOUNS[Math.floor(Math.random() * ALIAS_NOUNS.length)];
    const newAlias = `${randomAdj} ${randomNoun}`;

    const { error } = await supabase
      .from("profiles")
      .insert([
        { 
          id: currentUser.id, 
          email: currentUser.email, 
          campus_alias: newAlias, 
          avatar_seed: Math.random().toString(),
          campus_code: campusInfo.code
        }
      ]);

    if (error) {
      console.error("Error creating profile:", error);
      toast.error("Failed to create identity. Please try again.");
    } else {
      setAlias(newAlias);
      onVerified(currentUser, newAlias, campusInfo.code);
      toast.success(`Welcome, ${newAlias}!`);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (verifyingRef.current) return; // Block double entry
    
    verifyingRef.current = true;
    setLoading(true);
    
    // 1. Verify OTP
    const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'email'
    });

    if (error) {
        toast.error("Verification failed: " + error.message);
        setLoading(false);
        verifyingRef.current = false; // Reset lock
        return;
    }

    // 2. Success! Use the returned session
    if (data.session && data.user) {
         toast.success("Identity Verified!");
         setEmailSent(false); 
         
         // DIRECT ENTRY
         await handleUser(data.user);
         // Note: We don't reset verifyingRef here immediately because we are transitioning
         // and we don't want the AuthListener to interfere.
    } else {
        // Fallback
        await checkUser();
        verifyingRef.current = false;
    }
  };

  const handleMagicLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // 1. Strict Domain Check BEFORE sending email
    const isAllowedDomain = Object.keys(CAMPUS_MAPPING).some(domain => 
        email.trim().toLowerCase().endsWith(domain)
    );

    if (!isAllowedDomain) {
        toast.error("Access blocked: You must use a valid COMSATS email.");
        setLoading(false);
        return;
    }

    const { error } = await supabase.auth.signInWithOtp({
      email: email,
      options: {
        shouldCreateUser: true, 
        // No emailRedirectTo --> Requesting OTP Code
      },
    });

    if (error) {
      toast.error("Error: " + error.message);
    } else {
      setEmailSent(true);
      toast.success("Code sent! Check your inbox.");
    }
    setLoading(false);
  };

  // ------------------------------------------------------------------
  // RENDER: Check Inbox State
  // ------------------------------------------------------------------
  if(!user && emailsent) {
    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center animate-fade-in">
            <div className="bg-green-50 p-4 rounded-full mb-6">
                <Shield size={48} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Check your Inbox</h2>
            <p className="text-gray-600 max-w-md mb-8">
                We sent a verification code to <span className="font-semibold text-gray-900">{email}</span>. 
            </p>

            <form onSubmit={handleVerifyOtp} className="w-full max-w-xs mb-8">
                <input
                    type="text"
                    placeholder="Enter Code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.trim())}
                    className="w-full text-center text-2xl tracking-widest px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none mb-4 font-mono uppercase bg-white text-gray-900"
                    maxLength={8}
                    autoComplete="one-time-code"
                />
                <button
                    type="submit"
                    disabled={otpCode.length < 6 || loading}
                    className="w-full bg-purple-600 text-white rounded-xl py-3 font-bold hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                    {loading ? "Verifying..." : "Verify Code"}
                </button>
            </form>
            <button 
                onClick={() => setEmailSent(false)}
                className="text-purple-600 hover:text-purple-700 font-medium text-sm"
            >
                Entered wrong email? Try again
            </button>
        </div>
    )
  }

  // ------------------------------------------------------------------
  // RENDER: Login Selection Screen (Welcome Back)
  // ------------------------------------------------------------------
  if (showLoginOptions && sessionUser && !isSwitchingAccount) {
    const avatarUrl = sessionUser.profile?.avatar_url;
    const avatarSeed = sessionUser.profile?.avatar_seed;
    const userAlias = sessionUser.profile?.campus_alias || 'Anonymous User';
    
    return (
      <div className="flex items-center justify-center min-h-[70vh] px-6 py-12 animate-in fade-in zoom-in-95 duration-300">
        <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            
            {/* Desktop Left Side: Text & Welcome */}
            <div className="text-center md:text-left order-first">
               <div className="inline-flex items-center justify-center mb-6 md:justify-start">
                   <img src={logo} alt="Logo" className="w-16 h-16 object-contain opacity-80" />
               </div>
               <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4 leading-tight">
                <span className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-600 bg-clip-text text-transparent">
                  Welcome Back!
                </span>
              </h1>
              <p className="text-gray-600 text-lg md:text-xl font-medium max-w-md mx-auto md:mx-0">
                Ready to jump back into the conversation? Your campus is waiting.
              </p>
            </div>

            {/* Right Side: Saved Account Card */}
            <div className="w-full max-w-md mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 p-8 relative overflow-hidden transition-colors">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-purple-500 to-indigo-500"></div>

            {/* User Avatar */}
            <div className="flex flex-col items-center mb-8 mt-2">
                <div className="w-28 h-28 rounded-full overflow-hidden bg-gradient-to-br from-purple-100 to-indigo-100 mb-5 ring-4 ring-offset-2 ring-purple-50 shadow-lg flex items-center justify-center">
                {avatarUrl ? (
                    <img 
                    src={avatarUrl} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                    />
                ) : avatarSeed ? (
                    <img 
                    src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${avatarSeed}`}
                    alt="Avatar"
                    className="w-full h-full"
                    />
                ) : (
                    <Ghost size={48} className="text-purple-300" />
                )}
                </div>
                
                {/* User Info */}
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {userAlias}
                </h2>
                <p className="text-sm text-gray-500 font-medium bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
                {sessionUser.user.email}
                </p>
            </div>
            
            {/* Continue Button */}
            <button 
                onClick={handleContinueAsUser}
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:shadow-xl hover:shadow-purple-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed mb-4 group"
            >
                {loading ? (
                <>
                    <Loader className="animate-spin" size={24} />
                    <span>Accessing Campus...</span>
                </>
                ) : (
                <>
                    <span>Continue as {userAlias.split(' ')[0]}</span>
                    <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform" />
                </>
                )}
            </button>
            
            {/* Login Another Account Button */}
            <button 
                onClick={handleLoginAnother}
                disabled={loading}
                className="w-full bg-white text-gray-600 px-6 py-3.5 rounded-xl font-semibold border-2 border-gray-100 hover:bg-gray-50 hover:border-gray-200 hover:text-gray-900 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-sm"
            >
                Login to another account
            </button>
            </div>
        </div>
        
        {/* Footer Text (Absolute bottom or fixed) */}
        <div className="absolute bottom-4 left-0 right-0 text-center">
            {/* <p className="text-xs text-gray-400 max-w-sm mx-auto leading-relaxed">
            By continuing, you agree to allow CUConnect to access your basic profile information.
            </p> */}
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // RENDER: Loading State (Initial Check)
  // ------------------------------------------------------------------
  // Only show this strict loading state if we are effectively checking and NOT showing options yet
  if (loading && !showLoginOptions && !user) {
     return (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="animate-pulse flex flex-col items-center">
            <Ghost size={48} className="text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">Connecting to campus...</p>
          </div>
        </div>
      );
  }

  // ------------------------------------------------------------------
  // RENDER: Default Login Form (No Session or Switching)
  // ------------------------------------------------------------------
  if (!user || isSwitchingAccount) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] px-6 py-12 animate-fade-in">
        <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-12 items-center">
            
            {/* Left Side: Branding */}
            <div className="text-center md:text-left mb-4 md:mb-0">
                {/* App Logo */}
                <div className="inline-flex items-center justify-center mb-6 md:justify-start">
                    <img src={logo} alt="CUConnect Logo" className="w-24 h-24 object-contain" />
                </div>
                
                {/* Brand Name */}
                <h1 className="text-6xl font-black tracking-tight mb-4">
                    <span className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-600 bg-clip-text text-transparent">
                    CUConnect
                    </span>
                </h1>
                
                {/* Tagline */}
                <p className="text-2xl text-gray-600 font-medium mb-6">
                    Where the campus comes alive. <span className="block text-sm font-bold text-indigo-600 mt-2 uppercase tracking-widest">ISB • LHR • ATD • SWL • WAH • ATK</span>
                </p>
                
                 <div className="hidden md:block">
                    {/* Simplified info if needed, but keeping it clean as requested */}
                </div>
            </div>

            {/* Right Side: Login Card */}
            <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign In</h2>
                <p className="text-gray-500 text-sm mb-6">
                Use your official <span className="font-semibold text-purple-600">COMSATS</span> email
                </p>
                
                <form onSubmit={handleMagicLogin} className="space-y-4">
                <div>
                    <input 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="registration-id@isbstudent.comsats.edu.pk"
                    className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all outline-none bg-white text-gray-900 placeholder:text-gray-400 font-medium"
                    required
                    />
                </div>
                
                <button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3.5 rounded-xl font-semibold hover:shadow-xl hover:shadow-purple-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                    {loading ? (
                    <>
                        <Loader className="animate-spin" size={20} />
                        <span>Sending...</span>
                    </>
                    ) : (
                    <>
                        <span>Continue</span>
                        <ArrowRight size={20} />
                    </>
                    )}
                </button>
                </form>
                
                <p className="mt-6 text-center text-xs text-gray-400">
                Passwordless • Secure • Instant
                </p>

                {/* Cancel / Back to Saved Account Button */}
                {isSwitchingAccount && sessionUser && (
                   <button 
                     onClick={() => setIsSwitchingAccount(false)}
                     className="w-full mt-4 text-gray-500 hover:text-gray-800 text-sm font-medium py-2 transition-colors flex items-center justify-center gap-2"
                   >
                     <ArrowRight size={16} className="rotate-180" />
                     Back to {sessionUser.profile?.campus_alias || 'saved account'}
                   </button>
                )}
            </div>
            
            {/* Disclaimer */}
            <p className="mt-6 text-center text-xs text-gray-400 leading-relaxed max-w-sm mx-auto">
                CUConnect is constantly evolving to serve the student community. <br/>
                Designed with excellence for COMSATS University.
            </p>
            </div>
        </div>
      </div>
    );
  }

  // Loading state while checking alias
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <div className="animate-pulse flex flex-col items-center">
        <Ghost size={48} className="text-gray-300 mb-4" />
        <p className="text-gray-500 font-medium">Fetching your secret identity...</p>
      </div>
    </div>
  );
};

export default CampusIdentity;
